import { Controller } from "stimulus"
import { extractHeadings, extractSectionedHeadings, renderTocList, renderSectionedTocList, scrollToHeading } from "../tiptap/toc"
import { buildFindReplaceBar } from "../tiptap/find_replace"

export default class extends Controller {
  static targets = ["body", "search", "status", "findHost", "headings", "findMode", "headingMode"]

  connect() {
    this._editors = new Set()
    // Server-rendered placeholder for the unresolved state, restored on every rebuild that
    // happens before a document editor is ready.
    this._loadingHtml = this.hasBodyTarget ? this.bodyTarget.innerHTML : ""
    this._onFind = event => {
      if (!this.hasFindHostTarget || document.body.classList.contains('editor-focus-mode')) return
      event.preventDefault()
      event.detail.bar = this.showFind(event.detail)
    }
    document.addEventListener('editor:find', this._onFind)
    this._onContentChanged = () => this.scheduleRebuild()
    this._onSectionLoaded = () => this.rebuild()
    this._onSelection = ({ editor }) => { this.activeEditor = editor; this.updateActiveHeading() }
    this._onClick = event => {
      if (event.target.closest("#outlineItem, .panel-nav-btn[data-panel='outline-panel-container']")) this.rebuild()
    }
    document.addEventListener("tiptap:content-changed", this._onContentChanged)
    document.addEventListener("tiptap:section-loaded", this._onSectionLoaded)
    document.addEventListener("tiptap:editor-ready", this._onSectionLoaded)
    document.addEventListener("click", this._onClick)
    this._initialBuild = setTimeout(() => this.rebuild(), 1000)
    // Turbo replaces/removes section frames; failed frames must remain in the denominator.
    const list = document.querySelector(".sections-list")
    if (list) {
      this._sectionObserver = new MutationObserver(() => this.scheduleRebuild())
      this._sectionObserver.observe(list, { childList: true })
    }
    this.rebuild()
  }

  disconnect() {
    document.removeEventListener('editor:find', this._onFind)
    this._findBar?.close({ restoreFocus: false })
    document.removeEventListener("tiptap:content-changed", this._onContentChanged)
    document.removeEventListener("tiptap:section-loaded", this._onSectionLoaded)
    document.removeEventListener("tiptap:editor-ready", this._onSectionLoaded)
    document.removeEventListener("click", this._onClick)
    clearTimeout(this._initialBuild)
    clearTimeout(this._rebuildDebounce)
    clearTimeout(this._loadingTimeout)
    this._sectionObserver?.disconnect()
    this.watchEditors([])
  }

  watchEditors(editors) {
    this._editors.forEach(editor => {
      if (!editors.includes(editor)) {
        editor.off("selectionUpdate", this._onSelection)
        editor.off("focus", this._onSelection)
        this._editors.delete(editor)
      }
    })
    editors.forEach(editor => {
      if (!this._editors.has(editor)) {
        editor.on("selectionUpdate", this._onSelection)
        editor.on("focus", this._onSelection)
        this._editors.add(editor)
      }
    })
  }

  scheduleRebuild() {
    clearTimeout(this._rebuildDebounce)
    this._rebuildDebounce = setTimeout(() => this.rebuild(), 500)
  }

  filter() { this.rebuild() }

  // No document editor is ready yet, so the outline is unresolved: keep the placeholder shape
  // instead of claiming "No headings found", and fall back to an honest message if the editor
  // never reports in.
  showLoadingState() {
    if (this._loadingHtml) this.bodyTarget.innerHTML = this._loadingHtml
    this.bodyTarget.setAttribute("aria-busy", "true")
    if (this.hasStatusTarget) this.statusTarget.textContent = "Loading document headings…"
    if (this._loadingTimeout) return
    this._loadingTimeout = setTimeout(() => {
      this._loadingTimeout = null
      if (this._editors.size === 0) {
        this._unavailable = true
        this.showUnavailableState()
      }
    }, 20000)
  }

  showUnavailableState() {
    // Re-entrant: a search input, a panel click or a section mutation can rebuild the outline
    // while it is still unavailable. Keep the same honest message (and the focused Retry button)
    // instead of replacing it with a loading or empty state.
    if (!this.bodyTarget.querySelector(".sidebar-outline-unavailable")) {
      this.bodyTarget.innerHTML = `
        <div class="sidebar-frame-error sidebar-outline-unavailable" role="status">
          <span class="fal fa-exclamation-triangle sidebar-frame-error-icon" aria-hidden="true"></span>
          <p class="sidebar-frame-error-title">Couldn't load document headings.</p>
          <p class="sidebar-frame-error-detail">The document editor did not become available. Reload the editor or try again.</p>
          <button type="button" class="btn btn-sm btn-5 sidebar-frame-error-retry" data-action="toc-panel#retryLoading">Retry</button>
        </div>
      `
    }
    this.bodyTarget.setAttribute("aria-busy", "false")
    if (this.hasStatusTarget) this.statusTarget.textContent = "Document headings unavailable."
  }

  retryLoading(event) {
    event?.preventDefault()
    this._unavailable = false
    this.rebuild()
  }

  markResolved() {
    this._unavailable = false
    clearTimeout(this._loadingTimeout)
    this._loadingTimeout = null
    this.bodyTarget.setAttribute("aria-busy", "false")
  }

  showFind(options = {}) {
    if (!this.hasFindHostTarget) return
    const container = options.container || document.querySelector('.left-column')
    const getEditors = options.getEditors || (() => document.querySelector('[data-controller~="section-editor"]')
      ? extractSectionedHeadings(window.sectionEditorControllers).map(section => section.editor)
      : [window.tiptapEditor].filter(Boolean))
    const open = () => {
      this.setSearchMode(true)
      document.getElementById('currentSidebarTheme')?.classList.add('d-none')
      document.getElementById('comments-panel-container')?.classList.add('d-none')
      document.getElementById('outline-panel-container')?.classList.remove('d-none')
      this.element.dispatchEvent(new CustomEvent('editor:open-panel', { bubbles: true }))
    }
    open()
    this._findBar = buildFindReplaceBar(container, getEditors, {
      ...(options.options || {}), mount: this.findHostTarget, onFocus: open,
      onClose: () => { this._findBar = null; this.setSearchMode(false) },
    })
    return this._findBar
  }

  showHeadings() {
    this._findBar?.close({ restoreFocus: false })
    this.setSearchMode(false)
    this.rebuild()
    this.searchTarget.focus()
  }

  setSearchMode(find) {
    this.headingsTarget.classList.toggle('d-none', find)
    this.findHostTarget.classList.toggle('d-none', !find)
    this.findModeTarget.setAttribute('aria-pressed', String(find))
    this.headingModeTarget.setAttribute('aria-pressed', String(!find))
  }

  clearSearch() {
    this.searchTarget.value = ""
    this.rebuild()
    this.searchTarget.focus()
  }

  rebuild() {
    if (!this.hasBodyTarget) return
    const focused = this.bodyTarget.contains(document.activeElement) ? document.activeElement : null
    const oldIndex = Array.from(this.bodyTarget.querySelectorAll("button.toc-entry")).indexOf(focused)
    const query = this.hasSearchTarget ? this.searchTarget.value.trim().toLocaleLowerCase() : ""
    const sectioned = !!document.querySelector('[data-controller~="section-editor"]')
    const all = sectioned ? extractSectionedHeadings(window.sectionEditorControllers)
      : [{ editor: window.tiptapEditor, headings: extractHeadings(window.tiptapEditor) }]
    const editors = all.map(section => section.editor).filter(editor => editor && !editor.isDestroyed)
    this.watchEditors(editors)
    if (editors.length === 0) {
      // No usable document editor yet. After the loading timeout the outline stays in its honest
      // unavailable state: a search input, a panel click or a section mutation must not turn it
      // back into a claim that the document is empty. Only a real editor clears it, below.
      return this._unavailable ? this.showUnavailableState() : this.showLoadingState()
    }
    this._unavailable = false
    this.activeEditor = editors.includes(this.activeEditor) ? this.activeEditor : (editors.includes(window.tiptapEditor) ? window.tiptapEditor : editors[0])
    this._allEntries = all.flatMap(section => section.headings.map(heading => ({ ...heading, editor: section.editor })))
    const filtered = all.map(section => ({ ...section, headings: section.headings.filter(heading =>
      !query || (heading.text || "Untitled").toLocaleLowerCase().includes(query) || (section.sectionLabel || "").toLocaleLowerCase().includes(query)
    ) })).filter(section => !query || section.headings.length)
    const select = entry => {
      const breakpoint = Number(this.element.closest('[data-editor-sidebar-breakpoint-value]')?.dataset.editorSidebarBreakpointValue) || 1024
      if (window.innerWidth < breakpoint) this.element.dispatchEvent(new CustomEvent("editor:close-panel", { bubbles: true }))
      this.activeEditor = entry.editor
      scrollToHeading(entry.editor, entry.pos)
      this.updateActiveHeading()
    }
    const list = sectioned ? renderSectionedTocList(filtered, select)
      : renderTocList((filtered[0]?.headings || []).map(heading => ({ ...heading, editor: editors[0] })), select)
    this.bodyTarget.replaceChildren(list)
    if (!list.querySelector("button.toc-entry")) {
      const empty = document.createElement("p")
      empty.className = "toc-empty-state text-muted"
      empty.textContent = query ? "No matching headings in the loaded document." : "No headings found. Add headings (H1–H4) to see the document structure."
      this.bodyTarget.replaceChildren(empty)
    }
    this.markResolved()
    if (this.hasStatusTarget) {
      const total = document.querySelectorAll('.sections-list > turbo-frame').length
      this.statusTarget.textContent = sectioned ? `${editors.length} of ${total} sections loaded. ${editors.length < total ? 'Results cover loaded sections only.' : 'All sections loaded.'}`
        : editors.length ? "All document headings loaded." : "Loading document headings…"
    }
    this.updateActiveHeading()
    if (focused) {
      const buttons = Array.from(this.bodyTarget.querySelectorAll("button.toc-entry"))
      const replacement = buttons.find(button => button.tocEntry.editor === focused.tocEntry?.editor && button.tocEntry.pos === focused.tocEntry?.pos) || buttons[Math.min(oldIndex, buttons.length - 1)]
      if (replacement) replacement.focus({ preventScroll: true })
      else if (this.hasSearchTarget) this.searchTarget.focus({ preventScroll: true })
    }
  }

  updateActiveHeading() {
    const editor = this.activeEditor
    const pos = editor && !editor.isDestroyed ? editor.state.selection.from : -1
    const current = (this._allEntries || []).filter(entry => entry.editor === editor && entry.pos <= pos).pop()
    this.bodyTarget.querySelectorAll("button.toc-entry").forEach(button => {
      const active = !!current && button.tocEntry.editor === editor && button.tocEntry.pos === current.pos
      button.classList.toggle("active", active)
      if (active) button.setAttribute("aria-current", "location")
      else button.removeAttribute("aria-current")
    })
  }

  navigate(event) {
    const buttons = Array.from(this.bodyTarget.querySelectorAll("button.toc-entry"))
    if (!buttons.length) return
    const index = buttons.indexOf(document.activeElement)
    let next
    if (event.key === "ArrowDown") next = buttons[Math.min(index + 1, buttons.length - 1)]
    else if (event.key === "ArrowUp") next = index <= 0 ? this.searchTarget : buttons[index - 1]
    else if (index >= 0 && event.key === "Home") next = buttons[0]
    else if (index >= 0 && event.key === "End") next = buttons[buttons.length - 1]
    if (next) { event.preventDefault(); next.focus() }
  }
}
